/** Shared PortaledModal + card chrome (rounded card, shadow, close-X button) used by Variant B's modals. */

import React from "react";
import PortaledModal from "../../../components/Modals/PortaledModal";
import { useShellId } from "./shellContext";

interface VariantBModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  maxWidthPx?: number;
  // Anchors the card to the bottom edge of the mobile viewport (full-bleed,
  // rounded top corners only, internal scroll) instead of the default
  // centered floating card. Desktop/lg always keeps the centered card, capped to
  // the viewport and scrolling inside so tall content (HL-12093) stays reachable.
  mobileBottomSheet?: boolean;
  /** The mobile bottom sheet floats off the screen edges, all corners rounded. */
  floatingSheet?: boolean;
  // Centered card spans the full viewport width below md (no side gaps);
  // maxWidthPx only applies from md up.
  mobileFullWidth?: boolean;
  shouldBackgroundClickHideModal?: boolean;
  /** Overlay dimmer color — keep light enough that the page under the modal stays readable. */
  backgroundColor?: string;
  /** DOM id for the modal card; the close button gets `${modalId}_close`. */
  modalId?: string;
  /** HL-12357: slide the card up on open (the bottom sheet from off-screen, the centered card a short way). */
  animateIn?: boolean;
  /** No card padding and no close-X; children lay out their own header (with ModalCloseButton) and footer. */
  bare?: boolean;
  children: React.ReactNode;
}

// `backwards` fill: no transform is left on the card once it lands, so nothing inside is re-anchored.
const ANIMATE_IN_STYLES = `
  @keyframes vb-modal-sheet-up { from { transform: translateY(100%); } to { transform: none; } }
  @keyframes vb-modal-card-up { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: none; } }
  .vb-modal-animate-sheet { animation: vb-modal-sheet-up 320ms cubic-bezier(0.22, 1, 0.36, 1) backwards; }
  .vb-modal-animate-card { animation: vb-modal-card-up 280ms cubic-bezier(0.22, 1, 0.36, 1) backwards; }
  @media (min-width: 1024px) { .vb-modal-animate-sheet { animation-name: vb-modal-card-up; animation-duration: 280ms; } }
  @media (prefers-reduced-motion: reduce) { .vb-modal-animate-sheet, .vb-modal-animate-card { animation: none; } }
`;

export const ModalCloseButton: React.FC<{ cardId?: string; onClose: () => void; className?: string }> = ({
  cardId,
  onClose,
  className = "",
}) => (
  <button
    id={cardId ? `${cardId}_close` : undefined}
    onClick={onClose}
    aria-label="Close"
    className={`text-[#99a3b2] cursor-pointer hover:text-[#333] active:scale-95 transition ${className}`}
    style={{ border: "none", background: "transparent", padding: 0 }}
  >
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </button>
);

const VariantBModalShell: React.FC<VariantBModalShellProps> = ({
  isOpen,
  onClose,
  maxWidthPx = 420,
  mobileBottomSheet = false,
  floatingSheet = false,
  mobileFullWidth = false,
  shouldBackgroundClickHideModal = true,
  backgroundColor = "rgba(8, 8, 10, 0.35)",
  modalId,
  animateIn = false,
  bare = false,
  children,
}) => {
  const cardId = useShellId(modalId);
  if (!isOpen) return null;

  return (
    <PortaledModal
      shouldShowModal={isOpen}
      hideModal={onClose}
      shouldBackgroundClickHideModal={shouldBackgroundClickHideModal}
      backgroundColor={backgroundColor}
    >
      {animateIn && <style>{ANIMATE_IN_STYLES}</style>}
      <div
        id={cardId}
        className={`${
          mobileBottomSheet
            ? `${
                floatingSheet
                  ? "left-3 right-3 bottom-[max(12px,env(safe-area-inset-bottom))] rounded-[24px] lg:inset-auto lg:w-full"
                  : "w-full left-0 right-0 bottom-0 rounded-t-[24px]"
              } max-w-none bg-white shadow-[0px_10px_40px_rgba(8,8,10,0.15)] fixed max-h-[85vh] overflow-y-auto overscroll-contain lg:relative lg:max-h-[calc(100dvh-48px)] lg:rounded-[24px] lg:max-w-[var(--vb-modal-max-w)]${bare ? "" : " p-6 pb-8 lg:pb-6"}`
            : // overflow-y-auto: content taller than the capped height scrolls inside
              // the card (keeping the bottom padding) instead of spilling off-screen.
              `relative w-full bg-white rounded-[24px]${bare ? "" : " p-6"} shadow-[0px_10px_40px_rgba(8,8,10,0.15)] flex flex-col max-h-[calc(100dvh-48px)] overflow-y-auto overscroll-contain${
                mobileFullWidth ? " max-md:w-screen md:max-w-[var(--vb-modal-max-w)]" : ""
              }`
        }${animateIn ? (mobileBottomSheet ? " vb-modal-animate-sheet" : " vb-modal-animate-card") : ""}`}
        style={
          mobileBottomSheet || mobileFullWidth
            ? ({ "--vb-modal-max-w": `${maxWidthPx}px` } as React.CSSProperties)
            : { maxWidth: `${maxWidthPx}px` }
        }
      >
        {!bare && <ModalCloseButton cardId={cardId} onClose={onClose} className="absolute top-5 right-8" />}
        {children}
      </div>
    </PortaledModal>
  );
};

export default VariantBModalShell;
